import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { apiFetch } from '@/lib/api-fetch'
import { DeliveryStateBadge, DELIVERY_STATE_META } from '@/components/customers/DeliveryStateBadge'
import type { CustomerContactValue, CustomerProfile } from '@/lib/customer-profile'
import { compactCustomerName, customerInitials, formatCustomerName } from '@/lib/customer-name'
import { ACTIVE_CUSTOMER_STATES, isCurrentCustomerOrder } from '@/lib/customer-state'
import { CustomerDeliveryCard } from '@/components/customers/CustomerDeliveryCard'
import { DataTable, type DataTableColumn } from '@/components/data-table/DataTable'
import type { AnalyticsCustomerOrder } from '@/lib/analytics'

type CustomerOrderColumn = 'order' | 'date' | 'products' | 'delivery' | 'amount'

function money(value: number): string {
  return `${Math.round(value).toLocaleString('ru-RU')} ₽`
}

function date(value?: string, withTime = false): string {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return new Intl.DateTimeFormat('ru-RU', withTime
    ? { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    : { day: '2-digit', month: 'short', year: 'numeric' }
  ).format(parsed).replace('.', '')
}

const CONTACTS_COLLAPSED = 2

function ContactGroup({ title, description, values, kind }: {
  title: string
  description?: string
  values: CustomerContactValue[]
  kind?: 'phone' | 'email'
}) {
  const [expanded, setExpanded] = useState(false)
  const hidden = values.length - CONTACTS_COLLAPSED
  const shown = expanded || hidden <= 0 ? values : values.slice(0, CONTACTS_COLLAPSED)
  return (
    <section className="surface-card p-5 min-h-[150px]" aria-label={title} data-contact-total={values.length}>
      <div className="eyebrow mb-3">{title}</div>
      {description && <p className="-mt-1.5 mb-3 text-[9px] leading-relaxed text-ink-faint">{description}</p>}
      {values.length === 0 ? (
        <p className="text-[12px] text-ink-faint">—</p>
      ) : (
        <div className="space-y-3">
          {shown.map((entry) => {
            const href = kind === 'phone' ? `tel:${entry.value}` : kind === 'email' ? `mailto:${entry.value}` : undefined
            const shownValue = title === 'Имена' ? formatCustomerName(entry.value) : entry.value
            const content = <span className="text-[13px] font-medium text-ink break-words">{shownValue}</span>
            return (
              <div key={`${entry.value}:${entry.orderId || ''}`}>
                {href ? <a href={href} className="hover:text-gold-deep">{content}</a> : content}
                <div className="mt-1 text-[9px] text-ink-faint">{entry.source}{entry.orderId ? ` · заказ №${entry.orderId}` : ''}</div>
              </div>
            )
          })}
          {hidden > 0 && (
            <button
              type="button"
              aria-expanded={expanded}
              onClick={() => setExpanded((value) => !value)}
              className="text-[11px] font-medium text-ink-muted underline decoration-rule underline-offset-2 hover:text-gold-deep"
            >
              {expanded ? 'Свернуть' : `Показать ещё ${hidden.toLocaleString('ru-RU')}`}
            </button>
          )}
        </div>
      )}
    </section>
  )
}

function CustomerProfileView({ profile }: { profile: CustomerProfile }) {
  const { customer } = profile
  const delivery = customer.delivery
  const cdekByOrder = new Map(profile.cdekOrders.map((entry) => [entry.orderId, entry.data]))
  const cdekByNumber = new Map(
    profile.cdekOrders.flatMap((entry) => entry.data.cdek_number
      ? [[entry.data.cdek_number, entry.data] as const]
      : [])
  )
  const shipmentByOrder = new Map(
    (delivery?.shipments || []).flatMap((shipment) => shipment.orderId
      ? [[shipment.orderId, shipment] as const]
      : [])
  )
  const currentOrders = customer.orders.filter((order) =>
    isCurrentCustomerOrder(order.status, shipmentByOrder.get(order.orderId)?.state)
  )
  const currentOrderIds = new Set(currentOrders.map((order) => order.orderId))
  const historyColumns: readonly DataTableColumn<AnalyticsCustomerOrder, CustomerOrderColumn>[] = [
    {
      id: 'order', label: 'Заказ', sortValue: (order) => order.orderId,
      render: (order) => <span className="flex items-center gap-2 whitespace-nowrap font-semibold tab-nums">№{order.orderId}{currentOrderIds.has(order.orderId) ? <span className="rounded-full bg-onyx px-1.5 py-0.5 text-[7px] uppercase tracking-[0.08em] text-ivory">Текущий</span> : null}</span>,
    },
    { id: 'date', label: 'Дата', sortValue: (order) => Date.parse(order.date), render: (order) => <time className="whitespace-nowrap text-ink-muted" dateTime={order.date}>{date(order.date)}</time> },
    {
      id: 'products', label: 'Изделия', sortValue: (order) => order.items.map((item) => item.name).join(', '),
      render: (order) => {
        const products = order.items.map((item) => `${item.name} × ${item.count}`).join(', ')
        return <span className="block max-w-[360px] truncate text-ink-muted" title={products || undefined}>{products || 'Состав недоступен'}</span>
      },
    },
    {
      id: 'delivery', label: 'Доставка', sortValue: (order) => shipmentByOrder.get(order.orderId)?.state || null,
      render: (order) => {
        const shipment = shipmentByOrder.get(order.orderId)
        return shipment ? <span className="flex flex-col items-start gap-1"><DeliveryStateBadge state={shipment.state} /><span className="text-[8px] text-ink-faint tab-nums">СДЭК {shipment.cdekNumber}</span></span> : <span className="text-ink-faint">Без накладной</span>
      },
    },
    { id: 'amount', label: 'Сумма', sortValue: (order) => order.amount, render: (order) => <span className="whitespace-nowrap text-[13px] font-semibold tab-nums">{money(order.amount)}</span> },
  ]
  const orphanActiveShipments = (delivery?.shipments || []).filter(
    (shipment) => ACTIVE_CUSTOMER_STATES.has(shipment.state) &&
      (!shipment.orderId || !currentOrderIds.has(shipment.orderId))
  )
  const hasCurrentOrders = currentOrders.length > 0 || orphanActiveShipments.length > 0
  const pastShipments = (delivery?.shipments || []).filter(
    (shipment) => !ACTIVE_CUSTOMER_STATES.has(shipment.state)
  ).slice(0, 1)

  return (
    <div className="h-full overflow-y-auto bg-ivory">
      <div className="max-w-[1320px] mx-auto px-3 sm:px-6 md:px-8 xl:px-12 py-4 sm:py-8 xl:py-10 pb-[calc(24px+env(safe-area-inset-bottom))]">
        <Link to="/analytics" className="hidden md:inline text-[11px] text-ink-muted hover:text-gold-deep">← Все покупатели</Link>

        <header className="md:mt-6 mb-4 sm:mb-6 flex flex-col lg:flex-row lg:items-start justify-between gap-3 sm:gap-5 lg:gap-6">
          <div className="flex items-start gap-3 sm:gap-4 min-w-0">
            <div className="size-10 sm:size-14 shrink-0 rounded-full bg-onyx text-ivory flex items-center justify-center text-[15px] sm:text-[21px] font-semibold">
              {customerInitials(customer.name)}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 title={formatCustomerName(customer.name)} className="max-w-[calc(100vw-82px)] sm:max-w-full truncate text-[21px] sm:text-[30px] leading-tight font-semibold tracking-tight text-ink">{compactCustomerName(customer.name)}</h1>
                {customer.isTop && <span className="px-2 py-1 rounded-full bg-onyx text-ivory text-[9px] font-semibold uppercase tracking-[0.1em]">Топ 20%</span>}
                {delivery && <DeliveryStateBadge state={delivery.state} count={delivery.activeShipmentCount} />}
              </div>
              <p className="mt-1 sm:mt-2 text-[10px] sm:text-[12px] text-ink-muted truncate">
                {[customer.city, profile.emails[0]?.value].filter(Boolean).join(' · ') || 'Профиль покупателя Маркета'}
              </p>
              <p className="mt-0.5 sm:mt-1 max-w-[calc(100vw-82px)] truncate text-[8px] sm:text-[9px] text-ink-faint">Контакты в накладных могут относиться к разным получателям.</p>
            </div>
          </div>
          <div className={`w-full lg:max-w-[360px] border px-3 py-2.5 sm:p-4 ${profile.nextAction.tone === 'attention' ? 'border-st-unpaid/30 bg-st-unpaid/5' : profile.nextAction.tone === 'positive' ? 'border-st-delivered/25 bg-st-delivered/5' : 'border-rule bg-paper'}`}>
            <div className="eyebrow">Следующее действие</div>
            <div className="mt-0.5 sm:mt-1 text-[12px] sm:text-[13px] font-semibold text-ink">{profile.nextAction.title}</div>
            <p className="mt-0.5 sm:mt-1 text-[9px] sm:text-[10px] leading-snug sm:leading-relaxed text-ink-muted">{profile.nextAction.description}</p>
          </div>
        </header>

        <section className="grid grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-3 mb-4 sm:mb-6">
          {[
            ['Покупок', customer.orderCount.toLocaleString('ru-RU'), `доставлено ${customer.completedOrderCount}`],
            ['Оборот', money(customer.totalSpent), `средний чек ${money(customer.averageCheck)}`],
            ['С нами с', date(customer.firstOrderAt), `последняя ${date(customer.lastOrderAt)}`],
            ['Перцентиль', `${customer.percentile}-й`, customer.isTop ? 'один из лучших покупателей' : 'по сумме покупок'],
          ].map(([label, value, note]) => (
            <article key={label} className="surface-card p-3 sm:p-5 min-h-[96px] sm:min-h-[116px] flex flex-col justify-between">
              <div className="eyebrow">{label}</div>
              <div><div className="text-[18px] sm:text-[22px] leading-tight font-semibold text-ink tab-nums">{value}</div><div className="mt-1 text-[8px] sm:text-[10px] leading-snug text-ink-faint">{note}</div></div>
            </article>
          ))}
        </section>

        <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-6">
          <ContactGroup title="Имена в заказах" values={profile.names} />
          <ContactGroup
            title="Телефоны в накладных"
            description="СДЭК указывает получателя конкретной отправки; это не обязательно постоянный телефон покупателя."
            values={profile.phones}
            kind="phone"
          />
          <ContactGroup title="Почта" values={profile.emails} kind="email" />
          <ContactGroup title="Адрес доставки" values={profile.addresses} />
        </section>

        {(hasCurrentOrders || pastShipments.length > 0) && (
          <section className="surface-card mb-6 overflow-hidden p-3 sm:p-5">
            <div className="px-1 sm:px-5 py-3 sm:py-4 border-b border-rule flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4">
              <div>
                <div className="eyebrow">Логистика</div>
                <h2 className="mt-1 text-[17px] font-semibold">{hasCurrentOrders ? 'Что происходит сейчас' : 'Последние доставки'}</h2>
              </div>
              <div className="text-[10px] text-ink-faint">обновляются вебхуками CDEK</div>
            </div>
            <div className="mt-4 space-y-4">
              {currentOrders.map((order) => {
                const shipment = shipmentByOrder.get(order.orderId)
                return <CustomerDeliveryCard
                  key={`current:${order.orderId}`}
                  current
                  order={order}
                  shipment={shipment}
                  cdek={cdekByOrder.get(order.orderId) || (shipment ? cdekByNumber.get(shipment.cdekNumber) : undefined)}
                />
              })}
              {orphanActiveShipments.map((shipment) => (
                <CustomerDeliveryCard
                  key={`current:${shipment.cdekNumber}`}
                  current
                  shipment={shipment}
                  cdek={cdekByNumber.get(shipment.cdekNumber)}
                />
              ))}
              {pastShipments.length > 0 && hasCurrentOrders && (
                <div className="eyebrow pt-2">Недавние завершённые доставки</div>
              )}
              {pastShipments.map((shipment) => (
                <CustomerDeliveryCard
                  key={`past:${shipment.cdekNumber}`}
                  current={false}
                  order={shipment.orderId ? customer.orders.find((order) => order.orderId === shipment.orderId) : undefined}
                  shipment={shipment}
                  cdek={cdekByNumber.get(shipment.cdekNumber)}
                />
              ))}
            </div>
            {delivery && delivery.recentTransitions.length > 0 && (
              <div className="mt-4 border-t border-rule bg-bone/25 px-4 py-4">
                <div className="eyebrow mb-3">История состояний</div>
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  {delivery.recentTransitions.map((transition) => (
                    <span key={transition.id} className="text-[10px] text-ink-muted">
                      {date(transition.occurredAt, true)} · {DELIVERY_STATE_META[transition.from].label} → {DELIVERY_STATE_META[transition.to].label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        <section className="surface-card overflow-hidden">
          <div className="px-5 py-4 border-b border-rule"><div className="eyebrow">История отношений</div><h2 className="mt-1 text-[17px] font-semibold">Заказы и покупки</h2></div>
          <DataTable caption="Заказы и изделия покупателя" columns={historyColumns} rows={customer.orders} rowKey={(order) => String(order.orderId)} loading={false} emptyMessage="История заказов недоступна." loadingMessage="Загружаем историю…" defaultSort={{ column: 'date', direction: 'descending' }} className="rounded-none border-0" />
        </section>
      </div>
    </div>
  )
}

export default function CustomerPage() {
  const { customerId = '' } = useParams<{ customerId: string }>()
  const [profile, setProfile] = useState<CustomerProfile | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'not-found' | 'error'>('loading')

  useEffect(() => {
    const controller = new AbortController()
    setState('loading')
    void apiFetch(`/api/customers/${encodeURIComponent(customerId)}`, { signal: controller.signal })
      .then(async (response) => {
        if (response.status === 404) {
          setState('not-found')
          return
        }
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        setProfile(await response.json() as CustomerProfile)
        setState('ready')
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === 'AbortError')) setState('error')
      })
    return () => controller.abort()
  }, [customerId])

  if (state === 'loading') return <div className="grid h-full place-items-center text-sm text-ink-muted">Загрузка покупателя…</div>
  if (state === 'not-found') return <div className="grid h-full place-items-center text-sm text-ink-muted">Покупатель не найден.</div>
  if (state === 'error' || !profile) return <div role="alert" className="grid h-full place-items-center text-sm text-red-700">Не удалось загрузить покупателя.</div>
  return <CustomerProfileView profile={profile} />
}
